import * as Yup from 'yup';

/**
 * Builds a Yup schema dynamically from a JSON configuration
 * @param {Object} fieldsConfig - Configuration object with field definitions and validations
 * @param {Object} context - Runtime context for conditional validations
 * @param {string} context.hotelId - Hotel ID
 * @param {string} context.hotelNationality - Nationality of the hotel
 * @param {string} context.passengerNationality - Nationality of the passenger (optional, calculated from isForeign if not provided)
 * @param {number} context.idx - Index of the passenger (0 for first, 1+ for others)
 * @param {boolean} context.isForeign - Override: if provided, uses this instead of calculating from nationalities
 * @returns {Object} Yup schema object
 */
export function buildYupSchema(fieldsConfig, context = {}) {
  const { hotelId, hotelNationality, idx = 0, passengerNationality, isForeign } = context;
  
  const schemaFields = {};

  for (const [fieldPath, fieldConfig] of Object.entries(fieldsConfig)) {
    // Check if this is a nested object (has sub-fields) or a field with validations
    const hasValidations = fieldConfig.validations;
    const hasNestedFields = !hasValidations && Object.keys(fieldConfig).some(
      key => fieldConfig[key] && typeof fieldConfig[key] === 'object' && fieldConfig[key].validations
    );

    let schema;

    if (hasValidations) {
      // Simple field with validations
      schema = buildBaseSchema(fieldConfig.validations);
      schema = applyRequiredRules(schema, fieldConfig.validations, context, false);
    } else if (hasNestedFields) {
      // Nested object - recursively build schemas for sub-fields
      const nestedSchemas = {};
      const hasNationalityRules = hasAnyNationalityRules(fieldConfig);
      
      for (const [nestedKey, nestedConfig] of Object.entries(fieldConfig)) {
        if (nestedConfig && nestedConfig.validations) {
          let nestedSchema = buildBaseSchema(nestedConfig.validations);
          // For nested fields, don't apply .when() here - we'll apply it to the parent object
          // Just apply non-nationality rules
          nestedSchema = applyBasicRules(nestedSchema, nestedConfig.validations);
          nestedSchemas[nestedKey] = nestedSchema;
        }
      }
      
      // Create the nested object schema
      schema = Yup.object().shape(nestedSchemas);
      
      // If any nested field has nationality-based rules, wrap the object with .when()
      if (hasNationalityRules && hotelNationality) {
        schema = schema.when('nationality', {
          is: (passNationality) => {
            return passNationality && 
                   passNationality.toLowerCase() !== hotelNationality.toLowerCase();
          },
          then: (objectSchema) => {
            // Apply required rules to all nested fields that need them
            const conditionalSchemas = {};
            for (const [nestedKey, nestedConfig] of Object.entries(fieldConfig)) {
              if (nestedConfig && nestedConfig.validations) {
                let fieldSchema = objectSchema.fields[nestedKey];
                fieldSchema = applyNationalityConditionalRules(fieldSchema, nestedConfig.validations, context, true);
                conditionalSchemas[nestedKey] = fieldSchema;
              }
            }
            return objectSchema.shape(conditionalSchemas);
          },
          otherwise: (objectSchema) => {
            // Apply non-foreign rules
            const conditionalSchemas = {};
            for (const [nestedKey, nestedConfig] of Object.entries(fieldConfig)) {
              if (nestedConfig && nestedConfig.validations) {
                let fieldSchema = objectSchema.fields[nestedKey];
                fieldSchema = applyNationalityConditionalRules(fieldSchema, nestedConfig.validations, context, false);
                conditionalSchemas[nestedKey] = fieldSchema;
              }
            }
            return objectSchema.shape(conditionalSchemas);
          }
        });
      }
    } else {
      // Skip if it's neither a field nor a nested object
      continue;
    }

    // Add the field to the schema (handle dot notation paths if any)
    setNestedField(schemaFields, fieldPath, schema);
  }

  return Yup.object().shape(schemaFields);
}

/**
 * Builds the base schema based on type (string, date, number)
 */
function buildBaseSchema(validations) {
  const {
    string,
    date,
    number,
    allow_null,
  } = validations;

  let schema;

  if (date) {
    schema = Yup.date();
    
    // Handle typeError for dates
    if (validations.typeError) {
      schema = schema.typeError(validations.typeError);
    }
    
    // Handle max-date-now
    if (validations['max-date-now']) {
      schema = schema.max(new Date(), 'yup.wrong-date');
    }
    
    // Handle min-date
    if (validations['min-date']) {
      schema = schema.min(new Date(validations['min-date']), 'yup.min-date');
    }
  } else if (number) {
    schema = Yup.number().typeError('yup.must-be-number');
    
    // Handle minimal and maximal for numbers
    if (validations['minimal-digits']) {
      schema = schema.min(validations['minimal-digits'], 'yup.minimal-digits');
    }
    if (validations['maximal-digits']) {
      schema = schema.max(validations['maximal-digits'], 'yup.maximal-digits');
    }
  } else {
    // Default to string
    schema = Yup.string();
    
    // Handle only-numbers-and-letters pattern
    if (validations['only-numbers-and-letters']) {
      schema = schema.matches(/^[0-9a-zA-Z]+$/, 'yup.only-numbers-and-letters');
    }
    
    // Handle minimal-letters
    if (validations['minimal-letters']) {
      schema = schema.min(validations['minimal-letters'], 'yup.minimal-2-letters');
    }
    
    // Handle maximal-letters
    if (validations['maximal-letters']) {
      schema = schema.max(validations['maximal-letters'], 'yup.maximal-50-letters');
    }
    
    // Handle email validation
    if (validations.email) {
      schema = schema.email('yup.email-invalid-format');
    }
    
    // Handle other patterns
    if (validations.pattern) {
      schema = schema.matches(new RegExp(validations.pattern), validations['pattern-message'] || 'yup.invalid-format');
    }
  }

  // Apply allow_null to any type
  if (allow_null) {
    schema = schema.nullable();
  }

  return schema;
}

/**
 * Applies conditional required rules based on context
 * Uses .when() to read 'nationality' field from form values dynamically
 */
function applyRequiredRules(schema, validations, context, isNested = false) {
  const {
    required,
    required_for_foreign,
    required_for_not_foreign,
    required_for_pax_0,
    required_for_pax_0_foreign,
    required_for_pax_0_not_foreign,
    required_for_pax_gt_0,
    required_for_pax_gt_0_foreign,
    required_for_pax_gt_0_not_foreign,
  } = validations;

  const { hotelNationality, idx = 0, isForeign: manualIsForeign } = context;

  // If globally required, apply it directly
  if (required) {
    return schema.required('yup.required');
  }

  // Check if we have any nationality-based rules that need .when()
  const hasNationalityRules = 
    required_for_foreign || 
    required_for_not_foreign || 
    required_for_pax_0_foreign || 
    required_for_pax_0_not_foreign || 
    required_for_pax_gt_0_foreign || 
    required_for_pax_gt_0_not_foreign;

  // If manual isForeign is provided, use static validation (no .when() needed)
  if (manualIsForeign !== undefined && !hasNationalityRules) {
    const isUserForeign = manualIsForeign;
    
    if (required_for_foreign && isUserForeign) {
      return schema.required('yup.required');
    }
    if (required_for_not_foreign && !isUserForeign) {
      return schema.required('yup.required');
    }
    
    // Passenger index checks with manual isForeign
    if (idx === 0) {
      if (required_for_pax_0_foreign && isUserForeign) {
        return schema.required('yup.required');
      }
      if (required_for_pax_0_not_foreign && !isUserForeign) {
        return schema.required('yup.required');
      }
    }
    
    if (idx > 0) {
      if (required_for_pax_gt_0_foreign && isUserForeign) {
        return schema.required('yup.required');
      }
      if (required_for_pax_gt_0_not_foreign && !isUserForeign) {
        return schema.required('yup.required');
      }
    }
  }

  // Use .when() to read 'nationality' field dynamically from form values
  if (hasNationalityRules && hotelNationality) {
    // For nested fields, use relative path to access parent fields
    const nationalityPath = isNested ? '../nationality' : 'nationality';
    
    return schema.when(nationalityPath, {
      is: (passNationality) => {
        // Check if passenger nationality is different from hotel nationality (case-insensitive)
        return passNationality && 
               passNationality.toLowerCase() !== hotelNationality.toLowerCase();
      },
      then: (fieldSchema) => {
        // When user IS foreign
        if (idx === 0) {
          // First passenger
          if (required_for_pax_0_foreign) {
            return fieldSchema.required('yup.required');
          }
        } else if (idx > 0) {
          // Additional passengers
          if (required_for_pax_gt_0_foreign) {
            return fieldSchema.required('yup.required');
          }
        }
        
        // All passengers (any index)
        if (required_for_foreign) {
          return fieldSchema.required('yup.required');
        }
        
        return fieldSchema;
      },
      otherwise: (fieldSchema) => {
        // When user is NOT foreign (local)
        if (idx === 0) {
          // First passenger
          if (required_for_pax_0_not_foreign) {
            return fieldSchema.required('yup.required');
          }
        } else if (idx > 0) {
          // Additional passengers
          if (required_for_pax_gt_0_not_foreign) {
            return fieldSchema.required('yup.required');
          }
        }
        
        // All passengers (any index)
        if (required_for_not_foreign) {
          return fieldSchema.required('yup.required');
        }
        
        return fieldSchema;
      }
    });
  }

  // Index-based rules (without nationality dependencies)
  if (idx === 0) {
    if (required_for_pax_0) {
      return schema.required('yup.required');
    }
  }

  if (idx > 0) {
    if (required_for_pax_gt_0) {
      return schema.required('yup.required');
    }
  }

  return schema;
}

/**
 * Check if any nested field has nationality-based validation rules
 */
function hasAnyNationalityRules(nestedFieldConfig) {
  for (const [, config] of Object.entries(nestedFieldConfig)) {
    if (config && config.validations) {
      const { required_for_foreign, required_for_not_foreign, required_for_pax_0_foreign, required_for_pax_0_not_foreign, required_for_pax_gt_0_foreign, required_for_pax_gt_0_not_foreign } = config.validations;
      if (required_for_foreign || required_for_not_foreign || required_for_pax_0_foreign || required_for_pax_0_not_foreign || required_for_pax_gt_0_foreign || required_for_pax_gt_0_not_foreign) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Apply only basic validation rules (non-conditional)
 */
function applyBasicRules(schema, validations) {
  const {
    required,
    required_for_pax_0,
    required_for_pax_gt_0,
  } = validations;

  if (required) {
    return schema.required('yup.required');
  }

  return schema;
}

/**
 * Apply nationality-based conditional rules
 * isForeign: true if user is foreign, false if local
 */
function applyNationalityConditionalRules(schema, validations, context, isForeign) {
  const {
    required,
    required_for_foreign,
    required_for_not_foreign,
    required_for_pax_0,
    required_for_pax_0_foreign,
    required_for_pax_0_not_foreign,
    required_for_pax_gt_0,
    required_for_pax_gt_0_foreign,
    required_for_pax_gt_0_not_foreign,
  } = validations;

  const { idx = 0 } = context;

  // Collect all applicable required rules
  let shouldBeRequired = false;
  let requirementReason = '';

  // Global required
  if (required) {
    shouldBeRequired = true;
    requirementReason = 'global required';
  }

  // Apply based on foreignness
  if (isForeign) {
    // User IS foreign
    if (required_for_foreign) {
      shouldBeRequired = true;
      requirementReason = requirementReason ? `${requirementReason} + required_for_foreign` : 'required_for_foreign';
    }

    if (idx === 0 && required_for_pax_0_foreign) {
      shouldBeRequired = true;
      requirementReason = requirementReason ? `${requirementReason} + required_for_pax_0_foreign` : 'required_for_pax_0_foreign';
    }

    if (idx > 0 && required_for_pax_gt_0_foreign) {
      shouldBeRequired = true;
      requirementReason = requirementReason ? `${requirementReason} + required_for_pax_gt_0_foreign` : 'required_for_pax_gt_0_foreign';
    }
  } else {
    // User is NOT foreign (local)
    if (required_for_not_foreign) {
      shouldBeRequired = true;
      requirementReason = requirementReason ? `${requirementReason} + required_for_not_foreign` : 'required_for_not_foreign';
    }

    if (idx === 0 && required_for_pax_0_not_foreign) {
      shouldBeRequired = true;
      requirementReason = requirementReason ? `${requirementReason} + required_for_pax_0_not_foreign` : 'required_for_pax_0_not_foreign';
    }

    if (idx > 0 && required_for_pax_gt_0_not_foreign) {
      shouldBeRequired = true;
      requirementReason = requirementReason ? `${requirementReason} + required_for_pax_gt_0_not_foreign` : 'required_for_pax_gt_0_not_foreign';
    }
  }

  // Index-based rules
  if (idx === 0 && required_for_pax_0) {
    shouldBeRequired = true;
    requirementReason = requirementReason ? `${requirementReason} + required_for_pax_0` : 'required_for_pax_0';
  }

  if (idx > 0 && required_for_pax_gt_0) {
    shouldBeRequired = true;
    requirementReason = requirementReason ? `${requirementReason} + required_for_pax_gt_0` : 'required_for_pax_gt_0';
  }

  // Apply the requirement if any condition matched
  if (shouldBeRequired) {
    if (schema.describe().nullable) {
      return schema.test('required-validation', 'yup.required', (value) => {
        return value !== null && value !== undefined && value !== '';
      });
    }
    return schema.required('yup.required');
  }

  return schema;
}

/**
 * Sets a value in a nested object using dot notation
 * e.g., "photos.dniFront" -> { photos: { dniFront: value } }
 */
function setNestedField(obj, path, value) {
  const keys = path.split('.');
  let current = obj;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!current[key]) {
      current[key] = {};
    }
    current = current[key];
  }

  current[keys[keys.length - 1]] = value;
}

/**
 * Helper to create a config for string fields
 */
export function stringField(config = {}) {
  return {
    validations: {
      string: true,
      allow_null: config.nullable ?? false,
      ...config,
    },
  };
}

/**
 * Helper to create a config for date fields
 */
export function dateField(config = {}) {
  return {
    validations: {
      date: true,
      allow_null: config.nullable ?? false,
      ...config,
    },
  };
}

/**
 * Helper to create a config for number fields
 */
export function numberField(config = {}) {
  return {
    validations: {
      number: true,
      allow_null: config.nullable ?? false,
      ...config,
    },
  };
}
